import { get } from "lodash";
import { useDeepEffect } from "@tvg/custom-hooks";
import { useLocation } from "react-router";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useSelector, batch, useDispatch } from "react-redux";
import { useCallback } from "react";
import {
  getBetType,
  getBetAmount,
  getBetSelections,
  getRaceOfBet
} from "@tvg/desktop-bet/src/store/selectors";
import {
  setBetAmount,
  setBetType,
  setRaceOfBet,
  setSelectionRequest,
  setSelectionScratched
} from "@tvg/desktop-bet/src/store/actions";
import {
  getUseMinimalBetAmount,
  getUserBetAmountPref,
  getUserBetTypePref
} from "@tvg/sh-lib-preferences/redux/selectors";
import BetUtils from "@tvg/utils/betSelection";
import { RaceProgram } from "@tvg/ts-types/Race";
import { getOnlyAvailableRaceBetTypes } from "@tvg/desktop-bet/src/utils/betUtils";
import { getAccountNumber } from "@urp/store-selectors";
import {
  betParamsType,
  getBetFromParams,
  hasParams,
  removeBetParams
} from "../utils/betParamsUtils";
import { getBlacklistedWagerTypes } from "../store/selectors";

const isValidRace = (
  currentRace: RaceProgram,
  trackAbbr: string | undefined,
  raceNumber: string | null
) => {
  if (!currentRace) {
    return false;
  }
  return currentRace.id === `${trackAbbr}-${raceNumber}`;
};

type UseBetValidation = (currentRace: RaceProgram, isLiveTv: boolean) => void;

const useBetValidation: UseBetValidation = (currentRace, isLiveTv = false) => {
  const params = useParams<{ trackAbbr: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const raceNumber = searchParams.get("race");
  const raceOfBet = useSelector(getRaceOfBet);
  const betType = useSelector(getBetType);
  const betAmount = useSelector(getBetAmount);
  const betSelections = useSelector(getBetSelections);
  const userAccountNumber = useSelector(getAccountNumber);
  const betTypePreference = useSelector(getUserBetTypePref);
  const BetAmountPreference = useSelector(getUserBetAmountPref);
  const MinimalBetAmount = useSelector(getUseMinimalBetAmount);
  const blacklistedBets = useSelector(getBlacklistedWagerTypes);

  const resetBets = useCallback(
    (race: RaceProgram, betParams?: betParamsType) => {
      const wagerDefault = BetUtils.getDefaultBetType(
        race,
        betParams?.betType || betTypePreference,
        !!betParams?.betType || !!userAccountNumber
      );
      const amount = BetUtils.getBetAmount(
        wagerDefault,
        userAccountNumber,
        MinimalBetAmount,
        BetAmountPreference
      );

      const selections = BetUtils.setMatrixSizeByColumnCount(
        get(wagerDefault, "columnCount", 1)
      );

      const isWagerDefaultWhiteListed = !(
        blacklistedBets?.blacklistedBets || []
      ).includes(wagerDefault?.type.code);

      const defaultAmount =
        isWagerDefaultWhiteListed && betParams?.betAmount
          ? betParams.betAmount
          : amount;

      const defaultSelections =
        isWagerDefaultWhiteListed &&
        betParams?.betSelections &&
        betParams.betSelections?.length <= selections?.length
          ? selections.map((_, index) =>
              get(betParams.betSelections, index, [])
            )
          : selections;

      batch(() => {
        dispatch(setBetType(get(wagerDefault, "type.id", null)));
        dispatch(setBetAmount(defaultAmount));
        dispatch(setSelectionRequest(defaultSelections));
        dispatch(setSelectionScratched([...selections]));
        dispatch(setRaceOfBet(race.id || ""));
      });
    },
    [userAccountNumber, betTypePreference, blacklistedBets]
  );

  const resetBetAmount = useCallback(
    (wager, betAmountFromParam?: number) => {
      const amount = BetUtils.getBetAmount(
        wager,
        userAccountNumber,
        MinimalBetAmount,
        BetAmountPreference
      );
      dispatch(setBetAmount(betAmountFromParam || amount));
    },
    [
      userAccountNumber,
      betTypePreference,
      MinimalBetAmount,
      BetAmountPreference
    ]
  );

  const resetSelections = useCallback(
    (wager, betSelectionsFromParam) => {
      const selections = BetUtils.setMatrixSizeByColumnCount(
        get(wager, "columnCount", 1)
      );
      dispatch(setSelectionScratched(selections));
      dispatch(setSelectionRequest(betSelectionsFromParam || selections));
    },
    [userAccountNumber]
  );

  const setFinalPath = useCallback(() => {
    if (location?.hash) {
      navigate(`${location.pathname}${location.search}${location.hash}`);
    } else {
      setSearchParams(searchParams, { replace: true });
    }
  }, [location, searchParams]);

  useDeepEffect(() => {
    const filteredCurrentRace = {
      ...currentRace,
      wagerTypes: getOnlyAvailableRaceBetTypes(
        get(currentRace, "wagerTypes", []),
        blacklistedBets
      )
    };

    if (isValidRace(currentRace, params.trackAbbr, raceNumber)) {
      const betParams = getBetFromParams(searchParams, filteredCurrentRace);
      const isBetValid = currentRace.id === raceOfBet || hasParams(betParams);

      if (isBetValid) {
        const wager = BetUtils.getBetTypeFromRace(filteredCurrentRace, betType);

        const shouldResetBetType =
          !BetUtils.isValidBetType(filteredCurrentRace, betType) ||
          betParams.betType;

        const shouldResetBetAmount =
          !BetUtils.isValidBetAmount(wager, betAmount) || betParams.betAmount;

        const shouldResetSelection =
          !BetUtils.isValidBetSelection(wager, betSelections) ||
          betParams.betSelections;

        if (shouldResetBetType) {
          resetBets(filteredCurrentRace, betParams);
          if (!isLiveTv) {
            removeBetParams(searchParams);
            setFinalPath();
          }
          return;
        }

        if (shouldResetBetAmount) {
          resetBetAmount(wager, betParams.betAmount);
        }

        if (shouldResetSelection) {
          resetSelections(wager, betParams.betSelections);
        }

        if (!isLiveTv) {
          removeBetParams(searchParams);
          setFinalPath();
        }
      } else {
        resetBets(filteredCurrentRace);
        if (!isLiveTv) {
          removeBetParams(searchParams);
          setFinalPath();
        }
      }
    }
  }, [
    isLiveTv,
    params.trackAbbr,
    raceNumber,
    currentRace,
    betAmount,
    JSON.stringify(betSelections),
    betType,
    searchParams
  ]);
};

export default useBetValidation;
